import { useMemo } from 'react';
import { SparkActionSchema, SparkCreative, SparkFlow, SparkFlowStep, FlowActionReference } from '../types/sparkTypes';
import { formatActionTemplate } from '../utils/actionPresentation';

// Define the category order for sorting
const CATEGORY_ORDER: string[] = ['media', 'invent', 'update', 'derive'];

// Define a type for the fully resolved action
export interface ResolvedSparkAction extends SparkActionSchema {
  category: 'media' | 'invent' | 'update' | 'derive';
  input_type_fqn?: string | null;
  output_type_fqn?: string | null;
  formatted_label: string;
  formatted_description: string;
  logo_url?: string; // Partner logo URL
}

// --- Helper: Check Action Applicability ---
const isActionApplicableToSelection = (
  action: SparkActionSchema & { input_type_fqn?: string | null },
  selectedCreatives: SparkCreative[]
): boolean => {
  // When no creatives are selected, show actions that require no input (input_type_fqn null)
  if (selectedCreatives.length === 0) {
    return action.input_type_fqn == null;
  }

  // When there is a selection, exclude actions that require no input_type
  if (!action.input_type_fqn) {
    return false;
  }

  if (action.limits?.min_inputs && selectedCreatives.length < action.limits.min_inputs) {
    return false;
  }

  if (action.input_type_fqn) {
    const allCompatible = selectedCreatives.every(
      (creative) => creative._creative_type_fqn === action.input_type_fqn
    );
    if (!allCompatible) return false;
  }

  if (action.limits?.max_inputs && selectedCreatives.length > action.limits.max_inputs) {
    return false;
  }
  return true;
};

// --- The Hook ---
export const useSparkActions = (
  flowDefinition: SparkFlow | null,
  activeStepIndex: number, // Combined index (0=Briefing, 1=Insights, >1=Flow Steps)
  selectedCreatives: SparkCreative[]
): { actions: ResolvedSparkAction[] } => {

  const resolvedActions = useMemo(() => {
    if (!flowDefinition?.steps || !flowDefinition.actions || !flowDefinition.creative_types || activeStepIndex < 2) {
      return [];
    }

    const flowStepIndex = activeStepIndex - 2;
    const activeStep: SparkFlowStep | undefined = flowDefinition.steps[flowStepIndex];

    if (!activeStep) {
      return [];
    }

    const stepActionReferences: (FlowActionReference & { category: ResolvedSparkAction['category'] })[] = [];

    (activeStep.invent || []).forEach((ref) => stepActionReferences.push({ ...ref, category: 'invent' }));
    //(activeStep.media || []).forEach((ref) => stepActionReferences.push({ ...ref, category: 'media' }));
    (activeStep.update || []).forEach((ref) => stepActionReferences.push({ ...ref, category: 'update' }));
    (activeStep.derive || []).forEach((ref) => stepActionReferences.push({ ...ref, category: 'derive' }));

    const actionDefinitions = stepActionReferences
      .map((ref): ResolvedSparkAction | null => {
        const fullAction = flowDefinition.actions.find(
          (a) => a.name.toLowerCase() === ref.name.toLowerCase()
        );
        if (!fullAction) {
          console.warn(`Action definition not found for reference: ${ref.name}`);
          return null;
        }

        const formatContext = { input_type_fqn: ref.input_type_fqn, output_type_fqn: ref.output_type_fqn };

        const formatted_label = formatActionTemplate(
          fullAction.label,
          formatContext.input_type_fqn,
          formatContext.output_type_fqn,
          flowDefinition.creative_types
        );
        const formatted_description = formatActionTemplate(
          fullAction.description,
          formatContext.input_type_fqn,
          formatContext.output_type_fqn,
          flowDefinition.creative_types
        );

        return {
          ...fullAction,
          category: ref.category,
          input_type_fqn: ref.input_type_fqn,
          output_type_fqn: ref.output_type_fqn,
          formatted_label: formatted_label,
          formatted_description: formatted_description,
          logo_url: fullAction.logo_url,
        };
      })
      .filter((action): action is ResolvedSparkAction => action !== null);

    // Check applicability to selection
    const applicableActions = actionDefinitions
      .filter((action) =>
        isActionApplicableToSelection(action, selectedCreatives)
      );

    const sortedActions = applicableActions.sort((a, b) => {
      const indexA = CATEGORY_ORDER.indexOf(a.category);
      const indexB = CATEGORY_ORDER.indexOf(b.category);
      const effectiveIndexA = indexA === -1 ? Infinity : indexA;
      const effectiveIndexB = indexB === -1 ? Infinity : indexB;
      return effectiveIndexA - effectiveIndexB;
    });

    return sortedActions;

  }, [flowDefinition, activeStepIndex, selectedCreatives]);

  return {
    actions: resolvedActions,
  };
}; 
